import {
  applySnapshot,
  castToSnapshot,
  IAnyModelType,
  Instance,
  SnapshotOrInstance,
  types,
} from 'mobx-state-tree'
import { ActionStore } from './actions'
import { ActivityStore } from './activities'
import { AsyncTaskStore } from './async-tasks'
import { CategoryStore } from './categories'
import { ChangeStore } from './changes'
import { CheckinPerformanceQuestionStore } from './checkin-performance-questions'
import { CheckinSkillStore } from './checkin-skills'
import { CheckinStore } from './checkins'
import { CollectionStore } from './collections'
import { CommentStore } from './comments'
import { ContentLicenseGrantStore } from './content-license-grants'
import { DisciplineStore } from './disciplines'
import { DocStore } from './docs'
import { ExternalActivityStore } from './external-activities'
import { FeatureFlagStore } from './feature-flags'
import { FeatureGateStore } from './feature-gates'
import { FeedbackItemStore } from './feedback-item'
import { FeedbackRequestStore } from './feedback-request'
import { fetchSchemaVersions } from '../../api/fetch-schema-versions'
import { FrameworksSkillStore } from './frameworks-skills'
import { FrameworkStore } from './frameworks'
import { MetaStore } from './_meta'
import { NotificationStore } from './notifications'
import { OnboardingSurveyStore } from './onboarding-survey'
import { OrgLimitsStore } from './org-limits'
import { OrgStore } from './orgs'
import { OutcomeStore } from './outcomes'
import { PositionChangeStore } from './position-changes'
import { PositionSkillStore } from './position-skills'
import { PositionStore } from './positions'
import { ProfileSkillStore } from './profile_skills'
import { ReactionStore } from './reactions'
import { reference } from 'store/utils/reference'
import { RequirementStore } from './requirements'
import { ReflectionStore } from './reflections'
import { SalaryStore } from './salaries'
import { UserSalaryStore } from './user_salaries'
import { SkillLevelStore } from './skill-levels'
import { SkillStore } from './skills'
import { SkillVariantStore } from './skill-variants'
import { StoryItemStore } from './story-items'
import { StorePersister } from 'store/persist'
import { StoryStore } from './stories'
import { TeamStore } from './teams'
import { User, UserStore } from './users'
import { UserSkillStore } from './user_skills'
import { WinCategoryStore } from './win_categories'
import { WinStore } from './wins'
import { MembershipStore } from './memberships'
import { OperationsStore } from './operations'
import { Bootstrapper } from 'store/utils/bootstrapper'
import { exceptions } from 'src/utils/exceptions'

const rootStore = {
  _meta: types.optional(MetaStore, {}),
  actions: types.optional(ActionStore, {}),
  activities: types.optional(ActivityStore, {}),
  asyncTasks: types.optional(AsyncTaskStore, {}),
  categories: types.optional(CategoryStore, {}),
  changes: types.optional(ChangeStore, {}),
  checkins: types.optional(CheckinStore, {}),
  checkinSkills: types.optional(CheckinSkillStore, {}),
  checkinPerformanceQuestions: types.optional(
    CheckinPerformanceQuestionStore,
    {},
  ),
  collections: types.optional(CollectionStore, {}),
  comments: types.optional(CommentStore, {}),
  contentLicenseGrants: types.optional(ContentLicenseGrantStore, {}),
  disciplines: types.optional(DisciplineStore, {}),
  docs: types.optional(DocStore, {}),
  externalActivities: types.optional(ExternalActivityStore, {}),
  feedbackItems: types.optional(FeedbackItemStore, {}),
  feedbackRequests: types.optional(FeedbackRequestStore, {}),
  frameworks: types.optional(FrameworkStore, {}),
  frameworksSkills: types.optional(FrameworksSkillStore, {}),
  memberships: types.optional(MembershipStore, {}),
  notifications: types.optional(NotificationStore, {}),
  onboardingSurveys: types.optional(OnboardingSurveyStore, {}),
  orgs: types.optional(OrgStore, {}),
  outcomes: types.optional(OutcomeStore, {}),
  positionChanges: types.optional(PositionChangeStore, {}),
  positions: types.optional(PositionStore, {}),
  positionSkills: types.optional(PositionSkillStore, {}),
  profileSkills: types.optional(ProfileSkillStore, {}),
  reactions: types.optional(ReactionStore, {}),
  reflections: types.optional(ReflectionStore, {}),
  requirements: types.optional(RequirementStore, {}),
  salaries: types.optional(SalaryStore, {}),
  userSalaries: types.optional(UserSalaryStore, {}),
  skillLevels: types.optional(SkillLevelStore, {}),
  skills: types.optional(SkillStore, {}),
  skillVariants: types.optional(SkillVariantStore, {}),
  stories: types.optional(StoryStore, {}),
  storyItems: types.optional(StoryItemStore, {}),
  teams: types.optional(TeamStore, {}),
  users: types.optional(UserStore, {}),
  userSkills: types.optional(UserSkillStore, {}),
  winCategories: types.optional(WinCategoryStore, {}),
  wins: types.optional(WinStore, {}),
} as const

export type StoreModule = keyof typeof rootStore
export const storeModules = Object.keys(rootStore) as StoreModule[]

const persistableModules = [
  'disciplines',
  'docs',
  'frameworks',
  'orgs',
  'outcomes',
  'positions',
  'skillLevels',
  'skills',
  'teams',
  'users',
] satisfies StoreModule[]

const getPersistedModules = (flagEnabled: boolean): StoreModule[] =>
  flagEnabled ? persistableModules : ['users']

// Model containing just state, for access by modules
export const RootState = types.model('Root', {
  ...rootStore,
  currentUser: reference(User),
  bootstrapping: types.optional(types.boolean, true),
  featureFlags: types.optional(FeatureFlagStore, {}),
  featureGates: types.optional(FeatureGateStore, {}),
  orgLimits: types.optional(OrgLimitsStore, {}),
  operations: types.optional(OperationsStore, {}),
})

export type RootState = typeof RootState

// Root state tree, including root level actions
const RootStateWithActions = RootState.views((store) => ({
  get debug() {
    if (process.env.CI) return false
    if (process.env.SKIP_STORE_DEBUG) return false
    if (['development', 'staging'].includes(process.env.NODE_ENV || ''))
      return true
    if (store.featureFlags.featureEnabled('store-debug')) return true
    return false
  },
  get nonNullCurrentUser() {
    if (!store.currentUser) throw new Error('Current user not present in store')
    return store.currentUser
  },
})).actions((store) => ({
  setCurrentUser(userId: string) {
    store.currentUser = castToSnapshot(userId)
  },
  // Bulk load in state for a module
  load(
    resource: StoreModule,
    state: Record<string, SnapshotOrInstance<IAnyModelType>>,
  ) {
    store[resource].load(state)
  },
  completeBootstrapping() {
    store.bootstrapping = false
    exceptions.addBreadcrumb({
      action: 'Bootstrapping complete',
      category: 'Store',
      metadata: {
        now: new Date().toISOString(),
      },
    })
  },
  clear() {
    applySnapshot(store, {})
    Bootstrapper.reset()
    return StorePersister.destroyDatabase()
  },
  debugLog(...args: unknown[]) {
    if (!store.debug) return
    console.debug('[store]', ...args)
  },
}))

export const RootModel = RootStateWithActions.actions((store) => ({
  async bootstrap(userId: string, orgId: string | null) {
    try {
      exceptions.addBreadcrumb({
        action: 'Bootstrapping',
        category: 'Store',
        metadata: {
          now: new Date().toISOString(),
          ...persistableModules.reduce<Record<string, number>>(
            (acc, module) => {
              acc[module] = store[module].all.length
              return acc
            },
            {},
          ),
        },
      })

      const persistedModules = getPersistedModules(
        store.featureFlags.featureEnabled('persist_store'),
      )
      const persister = new StorePersister(
        userId,
        orgId,
        store,
        persistedModules,
        store.debug,
      )

      if (store.debug) console.time('bootstrapping')

      // Load cached data from IndexedDB first (fast, synchronous)
      // This allows the page to render immediately with cached data
      await persister.initialize()

      // Check if we have essential cached data (user data is critical)
      // If we have cached user data, we can show the page immediately
      const hasCachedUserData =
        store.users.all.length > 0 && store.users.data.has(userId)

      // Fetch schema versions (needed for bootstrap)
      const newSchemaVersions = await fetchSchemaVersions()

      // If we have cached user data, complete bootstrapping early
      // This allows the page to render immediately while fresh data loads in background
      if (hasCachedUserData) {
        store._meta.load(newSchemaVersions)
        store.completeBootstrapping()
      }

      // Bootstrap persisted modules in background
      // This will update the store with fresh data as it arrives
      // We don't await this if we already completed bootstrapping - let it run in background
      const bootstrapPromise = Promise.all(
        persistedModules.map((key) => {
          const storeModule = store[key]
          if (!('bootstrap' in storeModule)) return

          storeModule.completeRequest()
          return storeModule.bootstrap(newSchemaVersions)
        }),
      )

      if (!hasCachedUserData) {
        // Wait for bootstrap if we don't have cached data
        await bootstrapPromise
        store._meta.load(newSchemaVersions)
        store.completeBootstrapping()
      } else {
        // If we have cached data, bootstrap in background and update meta when done
        // Use async IIFE to handle the promise with await
        ;(async () => {
          try {
            await bootstrapPromise
            store._meta.load(newSchemaVersions)
          } catch (error) {
            console.error('Error during background bootstrap:', error)
          }
        })()
      }
      if (store.debug) console.timeEnd('bootstrapping')
    } catch (error) {
      console.error('Error during bootstrap:', error)
      // Always complete bootstrapping even if there's an error
      // This prevents the page from being stuck on the loading screen
      store.completeBootstrapping()
      throw error
    }
  },
}))

export type RootModel = Instance<typeof RootModel>
